/**
 * storeStatusUtils.js
 *
 * Computes the live operational status of a restaurant.
 *
 * Priority chain:
 *  1. isOnline === false           → "Temporarily Closed"  (overrides everything)
 *  2. isAcceptingOrders === false  → "Closed for Orders"   (overrides schedule)
 *  3. Schedule check               → parse openHours for today (+ overnight support)
 *  4. 24-hr snap rule              → gap ≤ 5 min between close & next open → treat as 24 hrs
 *  5. Closed by schedule           → find next opening time (up to 7 days ahead)
 */

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Convert "HH:MM" string to total minutes since midnight.
 * @param {string} timeStr e.g. "09:30"
 * @returns {number} minutes from midnight, or -1 if invalid
 */
const toMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return -1;
  const parts = timeStr.split(':');
  if (parts.length < 2) return -1;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return -1;
  return h * 60 + m;
};

/**
 * Format minutes-since-midnight to "H:MM AM/PM"
 * @param {number} totalMinutes
 * @returns {string}
 */
const formatTime = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

/**
 * Format "HH:MM" timeStr to "H:MM AM/PM"
 * @param {string} timeStr
 * @returns {string}
 */
export const formatTimeStr = (timeStr) => {
  const mins = toMinutes(timeStr);
  if (mins === -1) return timeStr || '—';
  return formatTime(mins);
};

/**
 * Format minutes-since-midnight to compact "H AM" / "H:MM AM" (drops :00 when on the hour)
 * @param {number} totalMinutes
 * @returns {string} e.g. "8 AM", "12:30 PM"
 */
const formatTimeCompact = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour12} ${period}` : `${hour12}:${String(m).padStart(2, '0')} ${period}`;
};

/** Short day abbreviations for compact label */
const SHORT_DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Check if `nowMins` falls within an open window defined by openMins → closeMins.
 * Handles both normal (08:00–22:00) and overnight (22:00–02:00) spans.
 *
 * @param {number} nowMins      current time in minutes since midnight
 * @param {number} openMins     open time in minutes since midnight
 * @param {number} closeMins    close time in minutes since midnight
 * @returns {boolean}
 */
const isWithinWindow = (nowMins, openMins, closeMins) => {
  if (openMins === closeMins) return true; // 24-hr open treated as always open
  if (closeMins > openMins) {
    // Normal span: e.g. 08:00 → 22:00
    return nowMins >= openMins && nowMins < closeMins;
  } else {
    // Overnight span: e.g. 22:00 → 02:00 (crosses midnight)
    return nowMins >= openMins || nowMins < closeMins;
  }
};

/**
 * Get the openHours entry for a given day name.
 * @param {Array} openHours  array of { day, openTime, closeTime }
 * @param {string} dayName   lowercase weekday e.g. "monday"
 * @returns {{ openTime: string, closeTime: string } | null}
 */
const getHoursForDay = (openHours, dayName) => {
  if (!Array.isArray(openHours)) return null;
  return openHours.find((h) => (h.day || '').toLowerCase() === dayName) || null;
};

/**
 * Main entry point.
 *
 * @param {object} restaurant  The details object from RestaurantContext
 * @returns {{
 *   isOpen: boolean,
 *   label: string,
 *   sublabel: string | null,
 *   is24hrs: boolean,
 *   isTemporarilyClosed: boolean,
 *   todayHours: { openTime: string, closeTime: string } | null,
 *   weekSchedule: Array<{ day: string, openTime: string, closeTime: string, is24hrs: boolean, isClosed: boolean }>
 * }}
 */
export const computeStoreStatus = (restaurant) => {
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const todayDayIndex = now.getDay(); // 0 = Sunday
  const todayName = DAY_NAMES[todayDayIndex];

  // ── Priority 1: isOnline override ──────────────────────────────────────────
  if (restaurant?.isOnline === false) {
    return _buildResult({
      isOpen: false,
      label: 'Temporarily Closed',
      sublabel: null,
      is24hrs: false,
      isTemporarilyClosed: true,
      restaurant,
      now,
      nowMins,
      todayName,
    });
  }

  // ── Priority 2: isAcceptingOrders override ─────────────────────────────────
  if (restaurant?.isAcceptingOrders === false) {
    return _buildResult({
      isOpen: false,
      label: 'Not Accepting Orders',
      sublabel: null,
      is24hrs: false,
      isTemporarilyClosed: true,
      restaurant,
      now,
      nowMins,
      todayName,
    });
  }

  const openHours = restaurant?.openHours;

  // No schedule configured → treat as always open
  if (!openHours || openHours.length === 0) {
    return _buildResult({
      isOpen: true,
      label: 'Open',
      sublabel: null,
      is24hrs: false,
      isTemporarilyClosed: false,
      restaurant,
      now,
      nowMins,
      todayName,
    });
  }

  // ── Priority 3: Schedule check (today + yesterday overnight) ───────────────
  const todayHours = getHoursForDay(openHours, todayName);

  // Also check yesterday's hours for overnight spans that bleed into today
  const yesterdayName = DAY_NAMES[(todayDayIndex + 6) % 7];
  const yesterdayHours = getHoursForDay(openHours, yesterdayName);

  let isOpen = false;
  let is24hrs = false;

  // Check today's window
  if (todayHours?.openTime && todayHours?.closeTime) {
    const openMins = toMinutes(todayHours.openTime);
    const closeMins = toMinutes(todayHours.closeTime);

    // ── 24-hr snap rule: gap between close and open ≤ 5 min ──────────────
    const gapMins = openMins > closeMins
      ? openMins - closeMins          // e.g. open=08:00, close=07:55 → gap=5
      : (24 * 60 - closeMins) + openMins; // wrap-around

    if (gapMins <= 5) {
      is24hrs = true;
      isOpen = true;
    } else {
      isOpen = isWithinWindow(nowMins, openMins, closeMins);
    }
  }

  // Check yesterday's overnight window (e.g. yesterday opened at 22:00, still in window at 01:00 today)
  if (!isOpen && yesterdayHours?.openTime && yesterdayHours?.closeTime) {
    const yOpenMins = toMinutes(yesterdayHours.openTime);
    const yCloseMins = toMinutes(yesterdayHours.closeTime);
    // Overnight span: closeTime < openTime means it crosses midnight
    if (yCloseMins < yOpenMins) {
      isOpen = nowMins < yCloseMins;
    }
  }

  if (isOpen) {
    // Compute when we close today (for sublabel)
    let closingLabel = null;
    if (!is24hrs && todayHours?.closeTime) {
      closingLabel = `Closes at ${formatTimeStr(todayHours.closeTime)}`;
    }
    return _buildResult({
      isOpen: true,
      label: is24hrs ? 'Open · 24 hrs' : 'Open',
      sublabel: closingLabel,
      is24hrs,
      isTemporarilyClosed: false,
      restaurant,
      now,
      nowMins,
      todayName,
    });
  }

  // ── Closed: find next opening time (scan up to 7 days) ────────────────────
  const nextOpenLabel = _findNextOpenLabel(openHours, now, nowMins, todayDayIndex);
  const nextOpenCompact = _findNextOpenCompact(openHours, now, nowMins, todayDayIndex);

  return _buildResult({
    isOpen: false,
    label: 'Closed',
    sublabel: nextOpenLabel,
    compactNextOpen: nextOpenCompact,
    is24hrs: false,
    isTemporarilyClosed: false,
    restaurant,
    now,
    nowMins,
    todayName,
  });
};

/**
 * Find the next scheduled opening time, scanning up to 7 days ahead.
 * Returns full label: "Opens Today at 8:00 AM" / "Opens Tomorrow at 6:00 PM" / "Opens Monday at …"
 */
const _findNextOpenLabel = (openHours, now, nowMins, todayDayIndex) => {
  for (let offset = 0; offset <= 7; offset++) {
    const dayIndex = (todayDayIndex + offset) % 7;
    const dayName = DAY_NAMES[dayIndex];
    const hours = getHoursForDay(openHours, dayName);
    if (!hours?.openTime) continue;

    const openMins = toMinutes(hours.openTime);
    if (openMins === -1) continue;

    if (offset === 0 && openMins <= nowMins) continue;

    const timeLabel = formatTimeStr(hours.openTime);
    if (offset === 0) return `Opens Today at ${timeLabel}`;
    if (offset === 1) return `Opens Tomorrow at ${timeLabel}`;
    const capitalizedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return `Opens ${capitalizedDay} at ${timeLabel}`;
  }
  return null;
};

/**
 * Find the next opening as a compact single-line label for the status pill.
 * Format: "Today 8 AM" / "Tmrw 12 AM" / "Mon 6 PM"
 */
const _findNextOpenCompact = (openHours, now, nowMins, todayDayIndex) => {
  for (let offset = 0; offset <= 7; offset++) {
    const dayIndex = (todayDayIndex + offset) % 7;
    const dayName = DAY_NAMES[dayIndex];
    const hours = getHoursForDay(openHours, dayName);
    if (!hours?.openTime) continue;

    const openMins = toMinutes(hours.openTime);
    if (openMins === -1) continue;

    if (offset === 0 && openMins <= nowMins) continue;

    const timeLabel = formatTimeCompact(openMins);
    if (offset === 0) return `Opens Today ${timeLabel}`;
    if (offset === 1) return `Opens Tmrw ${timeLabel}`;
    return `Opens ${SHORT_DAY[dayIndex]} ${timeLabel}`;
  }
  return null;
};

/**
 * Build the full result object including the 7-day week schedule.
 */
const _buildResult = ({ isOpen, label, sublabel, compactNextOpen, is24hrs, isTemporarilyClosed, restaurant, now, nowMins, todayName }) => {
  const openHours = restaurant?.openHours || [];
  const todayDayIndex = now.getDay();

  const weekSchedule = DAY_NAMES.map((day, i) => {
    const hours = getHoursForDay(openHours, day);
    const isToday = i === todayDayIndex;

    if (!hours?.openTime || !hours?.closeTime) {
      return { day, openTime: null, closeTime: null, is24hrs: false, isClosed: true, isToday };
    }

    const openMins = toMinutes(hours.openTime);
    const closeMins = toMinutes(hours.closeTime);

    const gapMins = openMins > closeMins
      ? openMins - closeMins
      : (24 * 60 - closeMins) + openMins;
    const dayIs24hrs = gapMins <= 5;

    return {
      day,
      openTime: hours.openTime,
      closeTime: hours.closeTime,
      is24hrs: dayIs24hrs,
      isClosed: false,
      isToday,
    };
  });

  const todayHours = getHoursForDay(openHours, todayName);

  return {
    isOpen,
    label,
    sublabel,
    compactNextOpen: compactNextOpen || null,
    is24hrs,
    isTemporarilyClosed,
    todayHours: todayHours || null,
    weekSchedule,
  };
};
